<template>
  <div class="billing-beneficiary-info">
    <box class="deposit-box" v-loading="loading">
      <div v-if="id" class="beneficiary-header">
        <div class="beneficiary-summary">
          <div class="beneficiary-heading">
            <div class="beneficiary-name">
              <template v-if="id">{{ beneficiaryName }}</template>
              <slot v-else name="title"><span>Beneficiary Info</span></slot>
            </div>
            <span v-if="id" class="type">{{ businessTypeValues[model.businessType] }}</span>
          </div>
          <div v-if="id" class="beneficiary-details">
            <div>{{ model.email }}</div>
            <div v-if="isEntrepreneur && model.taxIdentifierNumber">
              {{ get(model.taxIdentifierType, 'text') || 'Tax Number' }}: {{ model.taxIdentifierNumber }}
            </div>
          </div>
        </div>
        <div class="actions">
          <template v-if="!disabledActions && !isEdit">
            <button type="button" class="icon-action" aria-label="Edit beneficiary" title="Edit" @click="$emit('edit', id, true)">
              <img src="@/images/icon-edit.svg" alt="">
            </button>
            <button v-if="isDeleteAvailable" type="button" class="icon-action" aria-label="Delete beneficiary" title="Delete" @click="$emit('delete', id)">
              <img src="@/images/bin.svg" alt="">
            </button>
          </template>
          <button
            type="button"
            class="icon-action collapse-toggle"
            :class="{ expanded: visibleCollapse }"
            :aria-expanded="String(visibleCollapse)"
            :aria-controls="collapseId"
            :aria-label="visibleCollapse ? 'Collapse beneficiary info' : 'Expand beneficiary info'"
            @click="visibleCollapse = !visibleCollapse"
          >
            <img src="@/images/chevron-down-light.svg" alt="">
          </button>
        </div>
      </div>
      <component
        :is="id ? 'b-collapse' : 'div'"
        v-bind="id ? { id: collapseId, visible: visibleCollapse } : {}"
        @input="visibleCollapse = $event"
      >
        <div class="beneficiary-form" :class="{ 'beneficiary-form-new': !id }">
          <div v-if="!id" class="beneficiary-heading mb-20">
            <div class="beneficiary-name">Beneficiary Info</div>
            <dsp-button-group class="business-type-options">
              <dsp-button
                v-for="type in businessTypeOptions"
                :key="type.value"
                :class="{active: type.value === model.businessType}"
                @click="handleChangeBusinessType(type.value)"
              >
                <template>{{ type.text }}</template>
              </dsp-button>
            </dsp-button-group>
          </div>
          <div class="row" :class="{disabled}" :key="model.businessType">
            <div v-if="isLegal" class="col-md-6">
              <div class="form-group w-100">
                <div class="group-title">Company Name <b class="text-red">*</b></div>
                <dspInput
                  v-model="model.company"
                  v-validate="validationRules.company"
                  data-vv-as="Company Name"
                  class="w-100"
                  placeholder="Company Name"
                  :name="validation.COMPANY"
                  :errors-bag="errors"
                />
              </div>
            </div>
            <template v-else>
              <div class="col-md-4">
                <div class="form-group w-100">
                  <div class="group-title">First Name <b class="text-red">*</b></div>
                  <dspInput
                    v-model="model.firstName"
                    v-validate="validationRules.firstName"
                    data-vv-as="First Name"
                    class="w-100"
                    placeholder="First Name"
                    :name="validation.FIRST_NAME"
                    :errors-bag="errors"
                  />
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group w-100">
                  <div class="group-title">Last Name <b class="text-red">*</b></div>
                  <dspInput
                    v-model="model.lastName"
                    v-validate="validationRules.lastName"
                    data-vv-as="Last Name"
                    class="w-100"
                    placeholder="Last Name"
                    :name="validation.LAST_NAME"
                    :errors-bag="errors"
                  />
                </div>
              </div>
            </template>
            <div :class="!isLegal ? 'col-md-4' : 'col-md-6'">
              <div class="form-group w-100">
                <div class="group-title">Email <b class="text-red">*</b></div>
                <dspInput
                  v-model="model.email"
                  v-validate="validationRules.email"
                  data-vv-as="Email"
                  class="w-100"
                  placeholder="johndoe@mail.com"
                  :name="validation.EMAIL"
                  :errors-bag="errors"
                />
              </div>
            </div>
            <beneficiary-info-geo
              ref="geo"
              :country.sync="model.country"
              :state.sync="model.state"
              :city.sync="model.city"
            />
            <div class="col-md-8">
              <div class="form-group w-100">
                <div class="group-title">Beneficiary Address <b class="text-red">*</b></div>
                <dspInput
                  v-model="model.address"
                  v-validate="validationRules.address"
                  data-vv-as="Beneficiary Address"
                  class="w-100"
                  placeholder="Beneficiary Address"
                  :name="validation.ADDRESS"
                  :errors-bag="errors"
                />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group w-100">
                <div class="group-title">ZIP Code <b class="text-red">*</b></div>
                <dspInput
                  v-model="model.zipCode"
                  v-validate="validationRules.zipCode"
                  data-vv-as="ZIP Code"
                  class="w-100"
                  placeholder="ZIP Code"
                  :name="validation.ZIP_CODE"
                  :errors-bag="errors"
                />
              </div>
            </div>
            <template v-if="!isIndividual">
              <div class="col-md-6">
                <div class="form-group w-100">
                  <div class="group-title">Tax Identifier Type</div>
                  <dropdown
                    v-model="model.taxIdentifierType"
                    drop-value="Select..."
                    :options="taxIdentifierOptions"
                    @input="model.taxIdentifierNumber = ''"
                  >
                    <template #header="{ value }">
                      <span>{{ get(value, 'text') || value }}</span>
                    </template>
                    <template #item="{ option }">
                      <span>{{ get(option, 'text') }}</span>
                    </template>
                  </dropdown>
                </div>
              </div>
              <div class="col-md-6" v-if="model.taxIdentifierType">
                <div class="form-group w-100">
                  <div class="group-title">{{ model.taxIdentifierType.text }}</div>
                  <dspInput
                    v-model="model.taxIdentifierNumber"
                    v-validate="validationRules.tax"
                    class="w-100"
                    :data-vv-as="model.taxIdentifierType.text"
                    :placeholder="model.taxIdentifierType.text"
                    :name="validation.TAX"
                    :errors-bag="errors"
                  />
                </div>
              </div>
            </template>
            <div v-if="isEdit && !disabledActions" class="col-md-12 beneficiary-form-actions">
              <dsp-button v-if="isCancelAvailable" @click="handleCancel">
                Cancel
              </dsp-button>
              <dsp-button class="primary" :disabled="disabled" @click="model.businessType ? handleSubmit() : ''">
                Save
              </dsp-button>
            </div>
          </div>
        </div>
      </component>
    </box>
  </div>
</template>

<script>
import {cloneDeep, get, isEqual} from 'lodash';
import BeneficiaryInfoGeo from "@/components/billing/BeneficiaryInfoGeo.vue";
import {
  BillingBeneficiaryService,
  BusinessType,
  businessTypeOptions,
  businessTypeValues,
  defaultModel,
  taxIdentifierOptions,
  Validation,
  validationRules
} from "@/services/billingBeneficiaryService";

const service = new BillingBeneficiaryService();

export default {
  name: 'BeneficiaryInfo',

  components: {BeneficiaryInfoGeo},

  props: {
    id: {
      type: [Number, null],
      default: null
    },

    data: {
      type: Object,
      default: () => defaultModel
    },

    disabledActions: {
      type:Boolean,
      default: false
    },

    isEdit: {
      type:Boolean,
      default: false
    },

    isDeleteAvailable: {
      type: Boolean,
      default: true
    },

    isCancelAvailable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      model: cloneDeep(defaultModel),
      loading: false,
      visibleCollapse: this.isEdit || !this.id
    }
  },

  computed: {
    collapseId() {
      return `beneficiary-info-${this._uid}`;
    },

    beneficiaryName() {
      return this.isLegal ? this.model.company : [this.model.firstName, this.model.lastName].filter(Boolean).join(' ');
    },

    businessTypeOptions() {
      return businessTypeOptions;
    },

    businessTypeValues() {
      return businessTypeValues;
    },

    taxIdentifierOptions() {
      return taxIdentifierOptions;
    },

    isLegal() {
      return this.model.businessType === BusinessType.LEGAL;
    },

    isIndividual() {
      return this.model.businessType === BusinessType.INDIVIDUAL;
    },

    isEntrepreneur() {
      return this.model.businessType === BusinessType.ENTREPRENEUR;
    },

    validation() {
      return Validation;
    },

    validationRules() {
      return validationRules;
    },

    errors() {
      return this.$validator.errors;
    },

    disabled() {
      return this.disabledActions || !this.isEdit || !this.model.businessType;
    }
  },

  watch: {
    isEdit(value) {
      this.visibleCollapse = value;
    },

    data: {
      handler() {
        if (isEqual(this.model, this.data)) {
          return;
        }

        this.model = cloneDeep(this.data);
      },

      immediate: true
    },
  },

  mounted() {
    service.axios = this.$axios;
  },

  methods: {
    get,

    handleCancel() {
      this.model = cloneDeep(this.data);
      this.$emit('edit', this.id, false);
      this.$nextTick(() => {
        this.$validator.reset();
        if (this.$refs.geo) {
          this.$refs.geo.$validator.reset();
        }
      });
    },

    /**
     * @param {BusinessType} type
     *
     * @return {void}
     */
    handleChangeBusinessType(type) {
      this.model.businessType = type;
    },

    handleSubmit() {
      Promise.all([this.$validator.validate(), this.$refs.geo.$validator.validate()])
        .then(response => {
          const [primary, geo] = response;
          if (!primary || !geo) {
            return;
          }

          this.$emit('submit', cloneDeep(this.model), this.id);
        });
    },
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/variables';

.billing-beneficiary-info {
  font-size: 14px;

  .beneficiary-name,
  .type,
  .beneficiary-details,
  :deep(.form-group .group-title),
  :deep(.input-wrap input),
  :deep(.input-wrap input::placeholder),
  :deep(.dropdown .dropdown-trigger a),
  :deep(.dropdown .dropdown-item),
  :deep(.button-group .button),
  :deep(.beneficiary-form-actions .button),
  :deep(.help-text) {
    font-size: 12px;
  }

  .deposit-box {
    padding: 16px 22px;
    border: 1px solid #DFE3E8;
    border-radius: 6px;
    background: #fff;
  }

  .beneficiary-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .beneficiary-summary {
    min-width: 0;
  }

  .beneficiary-heading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    min-height: 24px;
  }

  .beneficiary-name {
    font-weight: 600;
    color: #212B36;
    overflow-wrap: anywhere;
  }

  .type {
    line-height: 18px;
    color: #919EAB;
    border-radius: 3px;
    background: #F6F6F8;
    padding: 0 7px;
  }

  .beneficiary-details {
    margin-top: 14px;
    line-height: 20px;
    color: #637381;
    overflow-wrap: anywhere;
  }

  .actions {
    display: flex;
    flex-shrink: 0;
    gap: 5px;
  }

  .icon-action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 5px;
    border: 0;
    border-radius: 3px;
    background: transparent;
    cursor: pointer;

    img {
      width: 12px;
      height: 12px;
    }

    &:hover {
      background: #F6F6F8;
    }

    &:focus-visible {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }
  }

  .collapse-toggle {
    img {
      transition: transform 200ms;
    }

    &.expanded img {
      transform: rotate(180deg);
    }
  }

  .beneficiary-form {
    padding-top: 20px;

    &.beneficiary-form-new {
      padding-top: 0;
    }
  }

  .beneficiary-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .button {
      min-width: 66px;
      padding-left: 16px;
      padding-right: 16px;
    }
  }

  .business-type-options :deep(.button.active) {
    background: $primary-color;
    border-color: $primary-color;
    color: #fff;
  }

  .form-group.w-100 {
    max-width: 100%;
  }

  :deep(.dropdown) {
    .dropdown-trigger {
      padding-top: 7px;
    }

    .dropdown-content {
      max-height: 300px;
      overflow-y: auto;

      .dropdown-item.active {
        pointer-events: none;

        &:focus {
          color: #fff;
        }
      }
    }

    .input-wrap {
      padding-left: 0;
      padding-right: 0;
    }
  }

  :deep(.input-wrap) {
    &.has-error {
      input {
        border: 1px solid $main-error;
      }
    }

    .help-text {
      position: relative;
    }
  }

  :deep(.row.disabled)   {
    .input-wrap,
    .dropdown {
      cursor: not-allowed;

      input,
      .dropdown-trigger {
        pointer-events: none;
        background-color: #f8f8f8;
        box-shadow: none;

        .icon {
          display: none;
        }
      }
    }
  }

  .button-group.disabled {
    cursor: not-allowed;

    button {
      pointer-events: none;
    }
  }
}
</style>
